import React, { useState, useEffect } from 'react';
import { ArrowRight, Linkedin } from 'lucide-react';
import { SOCIAL_LINKS, PROFILE_IMAGES } from '../constants';

const Hero: React.FC = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % PROFILE_IMAGES.length);
    }, 2000);
    return () => clearInterval(timer);
  }, []);

  return (
    <section className="pt-28 pb-16 md:pt-40 md:pb-24 px-6">
      <div className="container mx-auto max-w-6xl">
        <div className="flex flex-col md:flex-row items-center gap-12 md:gap-20">
          
          <div className="flex-1 space-y-6 md:space-y-8">
            <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 leading-[1.1] tracking-tight">
              <span className="sr-only">Jaclyn Konzelmann: </span>I build cool <br/>
              <span className="text-[#FF5E7E]">AI</span> <span className="text-[#FF7235]">Products</span>.
            </h1>
            
            <p className="text-base md:text-lg text-gray-600 max-w-lg leading-relaxed">
              Hi, I'm <strong className="text-gray-900 font-semibold">Jaclyn Konzelmann</strong>. 
              Director of Product Management at <a href="https://labs.google" target="_blank" rel="noopener noreferrer" className="text-[#FF5E7E] hover:opacity-80 transition-opacity font-semibold underline decoration-2 underline-offset-4">Google Labs</a>. 
              I'm passionate about taking ideas from zero to one and building the next generation of products.
            </p>

            <div className="flex flex-wrap gap-4 items-center pt-2">
              <a 
                href={SOCIAL_LINKS.substack}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#FF4D6D] to-[#FF7235] text-white rounded-full font-bold text-sm md:text-base shadow-md hover:shadow-lg hover:translate-y-[-2px] transition-all"
              >
                <span>Read Thursday Thoughts</span>
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
              
              <a 
                href={SOCIAL_LINKS.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-6 py-3 bg-white border border-gray-200 rounded-full text-gray-900 font-bold text-sm md:text-base shadow-sm hover:shadow-md hover:bg-gray-50 hover:translate-y-[-2px] transition-all"
              >
                <div className="text-[#0077B5] flex items-center">
                  <Linkedin size={18} fill="currentColor" />
                </div>
                <span>Connect on LinkedIn</span>
              </a>
            </div>
          </div>

          <div className="flex-1 relative w-full max-w-md md:max-w-none">
            <div 
              className="relative z-10 w-full max-w-md mx-auto aspect-square bg-gray-100 rounded-[2.5rem] overflow-hidden shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-500 isolate transform-gpu"
              style={{ WebkitMaskImage: '-webkit-radial-gradient(white, black)' }}
            >
               {PROFILE_IMAGES.map((img, index) => (
                 <img 
                   key={img.url}
                   src={img.url} 
                   alt={`Jaclyn Konzelmann - ${img.style}`}
                   width={1000}
                   height={1000}
                   decoding="async"
                   {...(index === 0 ? ({ fetchpriority: 'high' } as Record<string, string>) : { loading: 'lazy' as const })}
                   className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-in-out ${index === currentIndex ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}
                   style={{ willChange: 'opacity' }}
                 />
               ))}
               <div className="absolute bottom-6 right-6 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full text-[10px] font-black tracking-widest uppercase text-gray-500 shadow-sm border border-white/50 z-20">
                 {PROFILE_IMAGES[currentIndex].style}
               </div>
            </div>
            {/* Soft background accents */}
            <div className="absolute -top-12 -right-12 w-80 h-80 bg-[#FF5E7E]/5 rounded-full filter blur-[100px] -z-10"></div>
            <div className="absolute -bottom-12 -left-12 w-80 h-80 bg-[#FF7235]/5 rounded-full filter blur-[100px] -z-10"></div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default Hero;